/**
 * @file 教师管理主面板
 * @description 真实接口列表 + 科目展示、学员人数弹窗、行操作二次确认。
 *
 * @whenToUse 仅由 TeacherPage 组合。
 * @whenNotToUse 其他业务列表页。
 */

import { useRef, useState } from 'react'
import { MoreHorizontal, Plus } from 'lucide-react'
import { confirm } from '@/components/ui/confirm.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import { type DataTableColumn } from '@/components/ui/data-table.tsx'
import { DropdownMenu } from '@/components/ui/dropdown-menu.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { PageHeader } from '@/components/ui/page-header.tsx'
import { RequestTable, type RequestTableHandle } from '@/components/ui/request-table.tsx'
import { toast } from '@/components/ui/toast.tsx'
import {
  changeTeacherStatus,
  createTeacher,
  deleteTeacher,
  fetchTeacherList,
  resetTeacherPwd,
  updateTeacher,
  type TeacherItem,
} from '@/api/teacher.ts'
import { TeacherFormDialog } from '@/components/pages/teacher-page/teacher-form-dialog.tsx'
import { TeacherSearchFields } from '@/components/pages/teacher-page/teacher-search-fields.tsx'
import { TeacherStudentsDialog } from '@/components/pages/teacher-page/teacher-students-dialog.tsx'
import {
  EMPTY_TEACHER_QUERY,
  formatSubjects,
  type TeacherFormValues,
} from '@/components/pages/teacher-page/types.ts'

/**
 * 教师管理面板（含创建与行操作）
 */
export function TeacherPanel() {
  const tableRef = useRef<RequestTableHandle>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<TeacherItem | null>(null)
  const [studentsOpen, setStudentsOpen] = useState(false)
  const [studentsTarget, setStudentsTarget] = useState<TeacherItem | null>(null)

  /** 行操作成功后刷新列表 */
  const refresh = () => tableRef.current?.refresh()

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (row: TeacherItem) => {
    setEditing(row)
    setFormOpen(true)
  }

  /**
   * 打开学员列表弹窗；人数为 0 时仅轻提示
   * @param row 教师行
   */
  const openStudentsDialog = (row: TeacherItem) => {
    if (row.studentCount <= 0) {
      toast.show(`「${row.teacherName}」当前没有学员`)
      return
    }
    setStudentsTarget(row)
    setStudentsOpen(true)
  }

  const handleFormSubmit = async (values: TeacherFormValues) => {
    try {
      if (editing) {
        await updateTeacher({
          id: editing.id,
          teacherName: values.teacherName.trim(),
          subjects: values.subjects,
        })
        toast.success('已保存教师', values.teacherName)
      } else {
        await createTeacher({
          teacherName: values.teacherName.trim(),
          userName: values.userName.trim(),
          subjects: values.subjects,
          password: values.password.trim() || undefined,
        })
        toast.success(
          '已新增教师',
          values.password.trim() ? undefined : '密码默认 123456',
        )
      }
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '保存失败')
    }
  }

  /**
   * 重置密码（二次确认）
   * @param row 教师行
   */
  const handleResetPassword = async (row: TeacherItem) => {
    const ok = await confirm({
      title: '重置密码？',
      description: `将「${row.teacherName}」账号 ${row.userName} 的密码重置为 123456，确认是否重置？`,
      confirmText: '重置',
    })
    if (!ok) return
    try {
      await resetTeacherPwd(row.id)
      toast.success('密码已重置为 123456', row.teacherName)
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '重置失败')
    }
  }

  /**
   * 启用 / 禁用（二次确认）
   * @param row 教师行
   */
  const handleToggleEnabled = async (row: TeacherItem) => {
    const willEnable = row.status !== '1'
    const nextStatus = willEnable ? '1' : '0'
    const ok = await confirm({
      title: willEnable ? '启用教师？' : '禁用教师？',
      description: willEnable
        ? `启用后「${row.teacherName}」可正常登录使用，确认启用？`
        : `禁用后「${row.teacherName}」将无法登录使用，确认禁用？`,
      confirmText: willEnable ? '启用' : '禁用',
      danger: !willEnable,
    })
    if (!ok) return
    try {
      await changeTeacherStatus(row.id, nextStatus)
      toast.success(willEnable ? '已启用' : '已禁用', row.teacherName)
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '操作失败')
    }
  }

  /**
   * 删除教师（二次确认）
   * @param row 教师行
   */
  const handleDelete = async (row: TeacherItem) => {
    const ok = await confirm({
      title: '删除教师？',
      description: `将删除「${row.teacherName}」，此操作不可恢复。`,
      confirmText: '删除',
      danger: true,
    })
    if (!ok) return
    try {
      await deleteTeacher(row.id)
      toast.success('已删除', row.teacherName)
      refresh()
    } catch (error) {
      toast.danger(error instanceof Error ? error.message : '删除失败')
    }
  }

  const columns: DataTableColumn<TeacherItem>[] = [
    { key: 'teacherName', title: '教师名称', dataIndex: 'teacherName', minWidth: 100 },
    { key: 'userName', title: '教师账号', dataIndex: 'userName', minWidth: 120 },
    {
      key: 'subjects',
      title: '科目',
      minWidth: 140,
      render: (row) =>
        formatSubjects(row.subjects ? row.subjects.split(',') : []),
    },
    {
      key: 'status',
      title: '教师状态',
      minWidth: 88,
      render: (row) => (
        <Badge variant={row.status === '1' ? 'success' : 'danger'}>
          {row.status === '1' ? '启用' : '禁用'}
        </Badge>
      ),
    },
    {
      key: 'studentCount',
      title: '学员人数',
      minWidth: 96,
      render: (row) => (
        <button
          type="button"
          className="text-primary transition-opacity duration-150 hover:opacity-90"
          onClick={() => openStudentsDialog(row)}
        >
          {row.studentCount} 人
        </button>
      ),
    },
    { key: 'createTime', title: '创建时间', dataIndex: 'createTime', minWidth: 168 },
    {
      key: 'actions',
      title: '操作',
      fixed: 'right',
      width: 72,
      render: (row) => (
        <DropdownMenu
          align="end"
          trigger={
            <IconButton
              variant="ghost"
              aria-label={`${row.teacherName} 操作`}
              icon={<MoreHorizontal className="size-4" aria-hidden />}
            />
          }
          items={[
            {
              key: 'reset',
              label: '重置密码',
              onSelect: () => {
                void handleResetPassword(row)
              },
            },
            {
              key: 'toggle',
              label: row.status === '1' ? '禁用' : '启用',
              danger: row.status === '1',
              onSelect: () => {
                void handleToggleEnabled(row)
              },
            },
            {
              key: 'edit',
              label: '编辑',
              onSelect: () => openEdit(row),
            },
            { key: 'div-1', type: 'divider' },
            {
              key: 'delete',
              label: '删除',
              danger: true,
              onSelect: () => {
                void handleDelete(row)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="教师管理"
        description="管理教师档案、授课科目与启用状态。"
        extra={
          <Button
            icon={<Plus className="size-4" aria-hidden />}
            onClick={openCreate}
          >
            新增教师
          </Button>
        }
      />

      <div className="mt-4">
        <RequestTable
          ref={tableRef}
          request={fetchTeacherList}
          columns={columns}
          searchForm={{
            initialValues: EMPTY_TEACHER_QUERY,
            items: TeacherSearchFields,
          }}
        />
      </div>

      <TeacherFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        onSubmit={handleFormSubmit}
      />

      <TeacherStudentsDialog
        open={studentsOpen}
        onOpenChange={setStudentsOpen}
        teacher={studentsTarget}
      />
    </>
  )
}
